/**
 * Created by neo on 01.03.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Col, Container, Row } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { SubscribableEntry, SubscribableEntryUnion } from '../../../Model/Explore/SubscribableEntry';
import { ExploreEntryBuilder } from '../../../Model/Explore/ExploreEntryBuilder';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { Theme } from '../../../Theme/Theme';
import { Translation } from '../../../Components/Translation';
import { getMediaMedium, getMediaLarge, getMediaSmallest } from '../../../Model/Media/Media';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const PageContainer = styled.div`
  padding-bottom: 64px;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #3c3c3c 0%, #2e2e2e 100%);
  padding: 48px 0 64px;
  margin-bottom: 32px;
  color: white;
`;

const Header = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 16px;

  span {
    color: ${Theme.Colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  max-width: 600px;
  line-height: 1.6;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${Theme.Colors.primary};
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MonthSection = styled.div`
  margin-bottom: 40px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
`;

const MonthLabel = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
`;

const WebinarCount = styled.span`
  background: ${Theme.Colors.primary}15;
  color: ${Theme.Colors.primary};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const WebinarCard = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

    .card-image {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CardImageSection = styled.div`
  flex: 0 0 200px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    flex: none;
    height: 160px;
  }
`;

const CardImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ image }) =>
    image
      ? `url('${image}')`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
  transition: transform 0.4s ease;
`;

const DateBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: white;
  border-radius: 12px;
  padding: 8px 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const DateDay = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
`;

const DateMonth = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #666;
  letter-spacing: 0.5px;
`;

const CardContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardTime = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${Theme.Colors.primary};
  font-weight: 600;
  margin-bottom: 8px;

  i {
    font-size: 14px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const CardSubtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardHost = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.85rem;
  color: #888;

  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  background: #f8f9fa;
  border: 2px dashed #ddd;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Theme.Colors.primary};
    color: ${Theme.Colors.primary};
    background: ${Theme.Colors.primary}08;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  background: #f8f9fa;
  border-radius: 20px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0;
`;

const LoadingCard = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 16px;
  height: 140px;
  margin-bottom: 16px;
`;

const PAGE_SIZE = 20;

// Extracted component to avoid hooks-in-loops
const WebinarCardItem: React.FC<{
  webinar: SubscribableEntryUnion;
  onClick: (webinar: SubscribableEntryUnion) => void;
}> = observer(({ webinar, onClick }) => {
  const image = useLocalizedImage(webinar.images);
  const name = useLocalized(webinar.name);
  const shortDescription = useLocalized(webinar.shortDescription);
  const host = webinar.hosts[0];
  const startDate = dayjs(webinar.startDateTime);

  return (
    <WebinarCard onClick={() => onClick(webinar)}>
      <CardImageSection>
        <CardImage className="card-image" image={image ? getMediaMedium(image) || getMediaLarge(image) : undefined} />
        <DateBadge>
          <DateDay>{startDate.format('DD')}</DateDay>
          <DateMonth>{startDate.format('MMM')}</DateMonth>
        </DateBadge>
      </CardImageSection>
      <CardContent>
        <CardTime>
          <i className="bi bi-clock" />
          {webinar.startTime} • {startDate.format('dddd')}
        </CardTime>
        <CardTitle>{name}</CardTitle>
        <CardSubtitle>{shortDescription}</CardSubtitle>
        {host && (
          <CardHost>
            {host.image && <img src={getMediaSmallest(host.image)} alt={host.name} />}
            <span>{host.name}</span>
          </CardHost>
        )}
      </CardContent>
    </WebinarCard>
  );
});

export type AcademyScheduleScreenProps = {};

export const AcademyScheduleScreen: React.FC<AcademyScheduleScreenProps> = observer(() => {
  const navigate = useNavigate();
  const { t } = useTrans();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [webinars, setWebinars] = useState<SubscribableEntryUnion[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  // Initial load
  useEffect(() => {
    setLoading(true);
    ExploreEntryBuilder.upcoming({
      size: PAGE_SIZE,
      categories: ['academy_live'],
      startDateTime: dayjs().toDate(),
    })
      .then((result) => {
        setWebinars(result);
        setHasMore(result.length === PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || webinars.length === 0) return;

    setLoadingMore(true);
    const lastWebinar = webinars[webinars.length - 1];
    const lastDate = lastWebinar.startDateTime || dayjs().toDate();

    ExploreEntryBuilder.upcoming({
      size: PAGE_SIZE,
      categories: ['academy_live'],
      startDateTime: dayjs(lastDate).add(1, 'second').toDate(),
    })
      .then((result) => {
        setWebinars((prev) => [...prev, ...result]);
        setHasMore(result.length === PAGE_SIZE);
        setPage((p) => p + 1);
      })
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, webinars]);

  // Group webinars by month
  const webinarsByMonth = useMemo(() => {
    const groups = new Map<string, SubscribableEntryUnion[]>();
    webinars.forEach((webinar) => {
      const monthKey = dayjs(webinar.startDateTime).format('YYYY-MM');
      if (!groups.has(monthKey)) {
        groups.set(monthKey, []);
      }
      groups.get(monthKey)!.push(webinar);
    });
    return groups;
  }, [webinars]);

  const handleClickWebinar = useCallback(
    (entry: SubscribableEntryUnion) => {
      navigate(`/academy/${entry.id}`);
    },
    [navigate],
  );

  const totalUpcoming = webinars.length;
  const nextWebinar = webinars[0];
  const daysUntilNext = nextWebinar ? dayjs(nextWebinar.startDateTime).diff(dayjs(), 'day') : null;

  return (
    <PageContainer>
      <HeroSection>
        <Container>
          <Row>
            <Col xs={12} lg={8}>
              <Header>
                <Translation i18nKey="academySchedule.title" defaultValue="Academy" />{' '}
                <span>{t('academySchedule.titleHighlight', { defaultValue: 'Schedule' })}</span>
              </Header>
              <Description>
                {t('academySchedule.description', {
                  defaultValue:
                    'Learn more about health! In our monthly webinars, KINASTIC experts share their knowledge with you. You can ask them your questions and get practical health tips for your everyday life.',
                })}
              </Description>
              <StatsRow>
                <StatItem>
                  <StatValue>{totalUpcoming}</StatValue>
                  <StatLabel>{t('academySchedule.upcomingWebinars', { defaultValue: 'Upcoming Webinars' })}</StatLabel>
                </StatItem>
                {daysUntilNext !== null && (
                  <StatItem>
                    <StatValue>
                      {daysUntilNext === 0 ? t('academySchedule.today', { defaultValue: 'Today' }) : daysUntilNext}
                    </StatValue>
                    <StatLabel>
                      {daysUntilNext === 0
                        ? t('academySchedule.nextWebinar', { defaultValue: 'Next Webinar' })
                        : t('academySchedule.daysUntilNext', { defaultValue: 'Days Until Next' })}
                    </StatLabel>
                  </StatItem>
                )}
              </StatsRow>
            </Col>
          </Row>
        </Container>
      </HeroSection>

      <Container>
        {loading ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : webinars.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📅</EmptyIcon>
            <EmptyTitle>{t('academySchedule.noWebinars', { defaultValue: 'No Upcoming Webinars' })}</EmptyTitle>
            <EmptyDescription>
              {t('academySchedule.noWebinarsDescription', {
                defaultValue: 'Check back soon for new webinars from our experts.',
              })}
            </EmptyDescription>
          </EmptyState>
        ) : (
          <>
            {Array.from(webinarsByMonth.entries()).map(([monthKey, monthWebinars]) => (
              <MonthSection key={monthKey}>
                <MonthHeader>
                  <MonthLabel>{dayjs(monthKey, 'YYYY-MM').format('MMMM YYYY')}</MonthLabel>
                  <WebinarCount>
                    {monthWebinars.length} {monthWebinars.length === 1 ? 'webinar' : 'webinars'}
                  </WebinarCount>
                </MonthHeader>
                {monthWebinars.map((webinar) => (
                  <WebinarCardItem key={webinar.id} webinar={webinar} onClick={handleClickWebinar} />
                ))}
              </MonthSection>
            ))}

            {hasMore && (
              <LoadMoreButton onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <>
                    <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle" />
                    {t('academySchedule.loadMore', { defaultValue: 'Load More Webinars' })}
                  </>
                )}
              </LoadMoreButton>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
});
